import type {
  CatalogAuditAction,
  CatalogAuditRecord,
  CatalogProduct,
  CreateCategoryInput,
  CreateProductInput,
  DuplicateProductInput,
  ProductCategory,
  UpdateCategoryInput,
  UpdateProductInput,
} from '@emazy/shared-types';

import { categoryConfigs, mockCatalog } from '@/features/catalog/data/mockCatalog';

import type {
  AdminCatalogApi,
  AdminCategoryListInput,
  AdminProductListInput,
  AuditListInput,
  CatalogLifecycleInput,
} from './types';
import { AdminCatalogError } from './types';

const now = '2026-08-03T00:00:00.000Z';
let sequence = 100;
const id = (prefix: string) => `${prefix}-${++sequence}`;
const categories: ProductCategory[] = categoryConfigs.map((item, index) => ({
  id: `category-${item.slug}`,
  slug: item.slug,
  name: item.title,
  shortDescription: item.description,
  description: item.heroMessage,
  status: 'published',
  displayOrder: index,
  seo: { title: item.title, description: item.description },
  version: 1,
  createdAt: now,
  updatedAt: now,
  publishedAt: now,
}));
const types = {
  'wood-engraving': 'wood-engraving',
  ornaments: 'ornament',
  tshirts: 'tshirt',
} as const;
const modes = {
  'wood-engraving': 'wood-engraving',
  ornaments: 'ornament',
  tshirts: 'tshirt-print',
} as const;
const products: CatalogProduct[] = mockCatalog.map((item, index) => ({
  id: item.id,
  slug: item.slug,
  sku: item.id.toUpperCase(),
  categoryId: `category-${item.category}`,
  productType: types[item.category],
  name: item.name,
  shortDescription: item.shortDescription,
  description: item.description,
  status: 'published',
  basePrice: { amountMinor: item.startingPrice * 100, currency: 'USD' },
  badges: [...item.badges],
  featured: item.featured,
  ...(item.featured ? { featuredOrder: index } : {}),
  merchandisingOrder: index,
  customizable: item.customizable,
  customization: {
    mode: item.customizable ? modes[item.category] : 'none',
    textInputs: [],
    customerNotesAllowed: true,
  },
  options: [],
  variants: [],
  media: [
    {
      id: `${item.id}-media`,
      kind: 'image',
      role: 'primary',
      objectKey: `catalog/products/${item.slug}.svg`,
      altText: item.imageAlt,
      contentType: 'image/svg+xml',
      displayOrder: 0,
    },
  ],
  seo: { title: item.name, description: item.shortDescription },
  version: 1,
  createdAt: now,
  updatedAt: now,
  publishedAt: now,
}));
const audits: CatalogAuditRecord[] = [];

export class MockAdminCatalogApi implements AdminCatalogApi {
  async listCategories(input: AdminCategoryListInput) {
    return paginate(
      categories.filter((item) => item.status === input.status),
      input,
    );
  }
  async getCategory(categoryId: string) {
    return found(categories, categoryId, 'category');
  }
  async createCategory(input: CreateCategoryInput) {
    const value: ProductCategory = {
      ...input,
      id: id('category'),
      slug: input.slug ?? slugify(input.name),
      status: 'draft',
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    categories.push(value);
    record(value, 'category-created');
    return value;
  }
  async updateCategory(categoryId: string, input: UpdateCategoryInput) {
    const previous = await this.getCategory(categoryId);
    version(previous, input.expectedVersion);
    const { expectedVersion: _version, ...fields } = input;
    void _version;
    const value = {
      ...previous,
      ...fields,
      version: previous.version + 1,
      updatedAt: new Date().toISOString(),
    };
    replace(categories, value);
    record(value, 'category-updated');
    return value;
  }
  publishCategory(categoryId: string, input: CatalogLifecycleInput) {
    return this.categoryLifecycle(categoryId, input, 'published');
  }
  archiveCategory(categoryId: string, input: CatalogLifecycleInput) {
    return this.categoryLifecycle(categoryId, input, 'archived');
  }
  async listCategoryAudit(categoryId: string, input: AuditListInput = {}) {
    return paginate(
      audits.filter((item) => item.entityType === 'category' && item.entityId === categoryId),
      input,
    );
  }
  async listProducts(input: AdminProductListInput) {
    return paginate(
      products.filter(
        (item) =>
          item.status === input.status &&
          (!input.categoryId || item.categoryId === input.categoryId) &&
          (!input.exactSku || item.sku === input.exactSku.toUpperCase()) &&
          (!input.exactSlug || item.slug === input.exactSlug),
      ),
      input,
    );
  }
  async getProduct(productId: string) {
    return found(products, productId, 'product');
  }
  async createProduct(input: CreateProductInput) {
    const value = productValue(input);
    products.push(value);
    record(value, 'product-created');
    return value;
  }
  async updateProduct(productId: string, input: UpdateProductInput) {
    const previous = await this.getProduct(productId);
    version(previous, input.expectedVersion);
    const { expectedVersion: _version, ...fields } = input;
    void _version;
    const value: CatalogProduct = {
      ...previous,
      ...withNestedIds(fields),
      version: previous.version + 1,
      updatedAt: new Date().toISOString(),
    };
    replace(products, value);
    record(value, 'product-updated');
    return value;
  }
  publishProduct(productId: string, input: CatalogLifecycleInput) {
    return this.productLifecycle(productId, input, 'published');
  }
  archiveProduct(productId: string, input: CatalogLifecycleInput) {
    return this.productLifecycle(productId, input, 'archived');
  }
  async duplicateProduct(productId: string, input: DuplicateProductInput) {
    const source = await this.getProduct(productId);
    if (products.some((item) => item.sku === input.sku.toUpperCase()))
      throw new AdminCatalogError('sku-conflict', 'Another product uses this SKU.', 409);
    const value: CatalogProduct = {
      ...structuredClone(source),
      id: id('product'),
      name: input.name ?? `${source.name} Copy`,
      slug: input.slug ?? `${source.slug}-copy-${sequence}`,
      sku: input.sku.toUpperCase(),
      status: 'draft',
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: undefined,
      archivedAt: undefined,
    };
    products.push(value);
    record(value, 'product-duplicated');
    return value;
  }
  async listProductAudit(productId: string, input: AuditListInput = {}) {
    return paginate(
      audits.filter((item) => item.entityType === 'product' && item.entityId === productId),
      input,
    );
  }
  private async categoryLifecycle(
    categoryId: string,
    input: CatalogLifecycleInput,
    status: 'published' | 'archived',
  ) {
    const previous = await this.getCategory(categoryId);
    version(previous, input.expectedVersion);
    const value = {
      ...previous,
      status,
      version: previous.version + 1,
      updatedAt: new Date().toISOString(),
      ...(status === 'published'
        ? { publishedAt: new Date().toISOString() }
        : { archivedAt: new Date().toISOString() }),
    };
    replace(categories, value);
    record(value, status === 'published' ? 'category-published' : 'category-archived');
    return value;
  }
  private async productLifecycle(
    productId: string,
    input: CatalogLifecycleInput,
    status: 'published' | 'archived',
  ) {
    const previous = await this.getProduct(productId);
    version(previous, input.expectedVersion);
    if (
      status === 'published' &&
      previous.media.filter((item) => item.role === 'primary').length !== 1
    )
      throw new AdminCatalogError('not-publishable', 'Complete publication requirements.', 409, {
        publication: ['Exactly one primary media item is required.'],
      });
    const value: CatalogProduct = {
      ...previous,
      status,
      version: previous.version + 1,
      updatedAt: new Date().toISOString(),
      ...(status === 'published'
        ? { publishedAt: new Date().toISOString() }
        : { archivedAt: new Date().toISOString() }),
    };
    replace(products, value);
    record(value, status === 'published' ? 'product-published' : 'product-archived');
    return value;
  }
}

function productValue(input: CreateProductInput): CatalogProduct {
  return {
    ...withNestedIds(input),
    id: id('product'),
    slug: input.slug ?? slugify(input.name),
    status: 'draft',
    version: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}
function withNestedIds<
  T extends Omit<CatalogProduct, 'id' | 'slug' | 'status' | 'version' | 'createdAt' | 'updatedAt'>,
>(input: T): T {
  return {
    ...input,
    options: input.options.map((option) => ({
      ...option,
      id: option.id || id('option'),
      values: option.values.map((value) => ({ ...value, id: value.id || id('value') })),
    })),
    variants: input.variants.map((item) => ({ ...item, id: item.id || id('variant') })),
    media: input.media.map((item) => ({ ...item, id: item.id || id('media') })),
    customization: {
      ...input.customization,
      textInputs: input.customization.textInputs.map((item) => ({
        ...item,
        id: item.id || id('text'),
      })),
    },
  };
}
function paginate<T>(items: T[], input: { limit?: number; cursor?: string }) {
  const start = Number(input.cursor ?? 0);
  const limit = input.limit ?? 25;
  return Promise.resolve({
    items: items.slice(start, start + limit),
    nextCursor: start + limit < items.length ? String(start + limit) : null,
  });
}
function found<T extends { id: string }>(items: T[], itemId: string, kind: 'category' | 'product') {
  const value = items.find((item) => item.id === itemId);
  if (!value) throw new AdminCatalogError('not-found', `The ${kind} was not found.`, 404);
  return value;
}
function version(entity: { version: number }, expected: number) {
  if (entity.version !== expected)
    throw new AdminCatalogError('version-conflict', 'The record changed in another session.', 409);
}
function replace<T extends { id: string }>(items: T[], value: T) {
  items.splice(
    items.findIndex((item) => item.id === value.id),
    1,
    value,
  );
}
function record(entity: ProductCategory | CatalogProduct, action: CatalogAuditAction) {
  audits.unshift({
    id: id('audit'),
    entityType: action.startsWith('category') ? 'category' : 'product',
    entityId: entity.id,
    action,
    actorUserId: 'mock-admin',
    actorRoles: ['admin'],
    occurredAt: new Date().toISOString(),
    entityVersion: entity.version,
    summary: { name: entity.name },
  });
}
function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
